import React, { useState } from 'react';
import { PenLineIcon, PlusIcon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { useSocial } from '../../contexts/SocialContext';
import { AUDIENCE_LABEL, POST_KIND_LABEL } from '../../utils/social';
import type { Audience, PostKind } from '../../types/social';
const KINDS: PostKind[] = ['text', 'question', 'poll', 'recommendation', 'study_group', 'listing', 'gig'];
const AUDIENCES: Audience[] = ['connections', 'group', 'community', 'campus', 'institution'];

/**
 * Composer. Audience is chosen before publishing and is never defaulted to the
 * widest option — a post cannot reach a campus by accident.
 */
export function PostComposer() {
  const {
    publish,
    communities,
    me
  } = useSocial();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<PostKind>('text');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<Audience>('campus');
  const [spaceId, setSpaceId] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [preview, setPreview] = useState(false);
  const joined = communities.filter(community => community.joined);
  const needsSpace = audience === 'community' || audience === 'group';
  const ready = body.trim().length > 0 && (!needsSpace || spaceId !== '') && (kind !== 'poll' || options.filter(option => option.trim()).length >= 2);
  function reset() {
    setKind('text');
    setBody('');
    setAudience('campus');
    setSpaceId('');
    setOptions(['', '']);
    setPreview(false);
    setOpen(false);
  }
  if (!open) {
    return <Panel data-id="element-5644" data-mp="2h9x7rynih3a1">
        <div className="flex flex-wrap items-center gap-3" data-id="element-5645" data-mp="2hdupqgycrosc">
          <span aria-hidden className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-canvas text-body font-semibold text-ink" data-id="element-5646" data-mp="10wqjo32hetbs">
            {me.initials}
          </span>
          <Button variant="secondary" icon={<PenLineIcon className="h-4 w-4" data-id="element-5648" data-mp="33jnjreke1xxk" />} onClick={() => setOpen(true)} data-id="element-5647" data-mp="38igvp2k11gvm">
            Post, ask a question or run a poll
          </Button>
        </div>
      </Panel>;
  }
  return <Panel title="New post" action={<Button variant="ghost" size="sm" icon={<XIcon className="h-4 w-4" data-id="element-5651" data-mp="2ovj6wr80ogmp" />} onClick={reset} data-id="element-5650" data-mp="2tsjcl10yfhda">
          Cancel
        </Button>} data-id="element-5649" data-mp="2ttcdhld8fcni">
      <div className="space-y-4" data-id="element-5652" data-mp="2ktfrjri88rw5">
        <Field label="What kind of post is this?" htmlFor="post-kind" data-id="element-5653" data-mp="2kse7wyn5ax2t">
          <Select id="post-kind" value={kind} onChange={event => setKind(event.target.value as PostKind)} data-id="element-5654" data-mp="2fvnqa6voztfa">
            {KINDS.map(option => <option key={option} value={option} data-id="element-5655" data-mp="e26o3iqbzkrx">
                {POST_KIND_LABEL[option]}
              </option>)}
          </Select>
        </Field>

        <Field label={kind === 'poll' ? 'Poll question' : kind === 'question' ? 'Your question' : 'Post'} htmlFor="post-body" data-id="element-5656" data-mp="gnnwlsg7sn0n">
          <Textarea id="post-body" rows={4} value={body} onChange={event => setBody(event.target.value)} placeholder={kind === 'question' ? 'Where can I get a laptop screen replaced near campus?' : 'Say what you need to say'} data-id="element-5657" data-mp="ll655hqbl084" />
        </Field>

        {kind === 'poll' && <div data-id="element-5658" data-mp="2o4x1o1gyjzy">
            <p className="text-body-sm font-medium text-ink" data-id="element-5659" data-mp="2ppr7cjvg0xa">Options</p>
            <ul className="mt-2 space-y-2" data-id="element-5660" data-mp="7lem32ky4tn9">
              {options.map((option, index) => <li key={index} className="flex flex-wrap items-center gap-2" data-id="element-5661" data-mp="cnng829k21qy" data-mpi={index}>
                  <Input aria-label={`Option ${index + 1}`} value={option} onChange={event => setOptions(current => current.map((item, position) => position === index ? event.target.value : item))} className="min-w-0 flex-1" data-id="element-5662" data-mp="1j1389rqdlon0" />
                  {options.length > 2 && <Button variant="ghost" size="sm" onClick={() => setOptions(current => current.filter((_, position) => position !== index))} data-id="element-5663" data-mp="1qk2pprua163q">
                      Remove
                    </Button>}
                </li>)}
            </ul>
            <div className="mt-2" data-id="element-5664" data-mp="29cx52mvtb08h">
              <Button variant="ghost" size="sm" icon={<PlusIcon className="h-4 w-4" data-id="element-5666" data-mp="2o71t0lfh5cal" />} onClick={() => setOptions(current => [...current, ''])} data-id="element-5665" data-mp="2o963xnbsjbkd">
                Add option
              </Button>
            </div>
          </div>}

        <Field label="Who can see this?" htmlFor="post-audience" hint="Chosen deliberately. Nothing defaults to the widest audience." data-id="element-5667" data-mp="36xzsealjpx72">
          <Select id="post-audience" value={audience} onChange={event => {
          setAudience(event.target.value as Audience);
          setSpaceId('');
        }} data-id="element-5668" data-mp="2xx1l9jft99k9">
            {AUDIENCES.map(option => <option key={option} value={option} data-id="element-5669" data-mp="2o05dxh1netha">
                {AUDIENCE_LABEL[option]}
              </option>)}
          </Select>
        </Field>

        {needsSpace && <Field label="Which space?" htmlFor="post-space" data-id="element-5670" data-mp="2f723ziq7oyre">
            <Select id="post-space" value={spaceId} onChange={event => setSpaceId(event.target.value)} data-id="element-5671" data-mp="208zvcu4bpn3a">
              <option value="" data-id="element-5672" data-mp="25a72cqyn6i8w">Choose a space you belong to</option>
              {joined.map(community => <option key={community.id} value={community.id} data-id="element-5673" data-mp="1hd0zbptwdq6e">
                  {community.name}
                </option>)}
            </Select>
          </Field>}

        {preview && <div className="rounded-md border border-line bg-canvas p-4" data-id="element-5674" data-mp="17fd7b7noewrx">
            <div className="flex flex-wrap items-center gap-2" data-id="element-5675" data-mp="17d0ge4zxxc2q">
              <Badge plain data-id="element-5676" data-mp="2523gyxoj7j0w">{POST_KIND_LABEL[kind]}</Badge>
              <Badge tone="brand" data-id="element-5677" data-mp="202z9cbh99tco">{AUDIENCE_LABEL[audience]}</Badge>
            </div>
            <p className="mt-2 max-w-[72ch] whitespace-pre-wrap text-body text-ink" data-id="element-5678" data-mp="1v4fl24f6w5h5">
              {body || 'Nothing written yet.'}
            </p>
          </div>}

        <div className="flex flex-wrap gap-2" data-id="element-5679" data-mp="1q7ffdum954qk">
          <Button variant="primary" disabled={!ready} onClick={() => {
          publish({
            kind,
            body: body.trim(),
            audience,
            spaceId: needsSpace ? spaceId : null,
            pollOptions: kind === 'poll' ? options : undefined
          });
          reset();
        }} data-id="element-5680" data-mp="jpsd1sm2od3c">
            Publish
          </Button>
          <Button variant="secondary" onClick={() => setPreview(current => !current)} data-id="element-5681" data-mp="30ae48ton3l2m">
            {preview ? 'Hide preview' : 'Preview'}
          </Button>
        </div>
        {!ready && <p className="text-body-sm text-ink-muted" data-id="element-5682" data-mp="3a0yy63tdc1f7">
            {body.trim().length === 0 ? 'Write something before publishing.' : needsSpace && spaceId === '' ? 'Choose the space this belongs in.' : 'A poll needs at least two options.'}
          </p>}
      </div>
    </Panel>;
}